import { useQuery } from '@tanstack/react-query';
import { useActiveOrg } from '../providers/useActiveOrg';
import { useTypedOpenApi } from '../providers/useTypedOpenApi';
import { type PlanCatalog, planCatalogFrom, TABLE_PLAN_CATALOG } from './serverPlan';

/**
 * Keyed by organization: whether Pro's trial is still available is its owner's.
 *
 * `plans` rather than the old `plan-catalog`: that entry cached the built catalog, whose entitlement
 * `Set`s mobile's persister turned into `{}`, so a restored one broke `entitlements.has`.
 */
export const planCatalogKey = (clerkOrgId: string | null) => ['plans', clerkOrgId] as const;

/**
 * The plans, their prices, limits and entitlements, from `GET /plans`.
 *
 * The MDC-4517 table until the backend answers, or when it can't be reached, so the plans page
 * always has something to show.
 */
export const usePlanCatalog = (): PlanCatalog => {
  const { apiClient } = useTypedOpenApi();
  const { clerkOrgId } = useActiveOrg();

  const { data } = useQuery({
    queryKey: planCatalogKey(clerkOrgId),
    // The raw list is what is cached, since it survives mobile's JSON persister; the `Set`s are built on read.
    queryFn: async () => (await apiClient.get('/plans')).data,
    select: planCatalogFrom,
    enabled: clerkOrgId !== null,
    // Prices change in Stripe, rarely; the backend caches them for hours.
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });

  return data ?? TABLE_PLAN_CATALOG;
};
